import { signal } from '@preact/signals';
import { clipId } from './arabicVoice';
import { isSilent } from './silent';

// Plays the pre-rendered narration clips (George's voice, rendered by tools/narration) one after another, the Arabic
// voice's clips (keys "ar/<voice>/<id>", src/audio/arabicVoice.ts), and al-Minshāwī's recitation of the verses shown on
// their own (keys "qr/<sss><aaa>", tools/quran-audio, DECISIONS 109), each with their own list and folder.

interface Manifest {
  voice: string;
  /** `v` changes whenever a clip is re-rendered, so cached copies of the old recording are not replayed. */
  clips: Record<string, { dur: number; text: string; v?: string }>;
}

const base = () => `${import.meta.env.BASE_URL}audio/narration/`;
const arabicBase = () => `${import.meta.env.BASE_URL}audio/arabic/`;
const quranBase = () => `${import.meta.env.BASE_URL}audio/quran/`;
let audio: HTMLAudioElement | null = null;
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

// The two lists of clips load each on its own, so one that is slow or missing never holds up the other. A list that
// fails, or takes longer than 15 seconds, is asked for again the next time it is wanted, at most every 30 seconds.
type ListId = 'narration' | 'arabic' | 'quran';
const LIST_URL: Record<ListId, () => string> = {
  narration: () => `${base()}manifest.json`,
  arabic: () => `${arabicBase()}manifest.json`,
  quran: () => `${quranBase()}manifest.json`,
};
const lists: Record<ListId, Manifest['clips'] | null> = { narration: null, arabic: null, quran: null };
/** For each verse shown on its own (by src/audio/arabicVoice.ts's id of its text), the recitation's clips in order. */
let quranQuotes: Record<string, string[]> = {};
const asking: Partial<Record<ListId, Promise<void>>> = {};
const failedAt: Partial<Record<ListId, number>> = {};
/** Counts the lists loaded, so the buttons for clips that exist show as soon as their list arrives. */
export const clipsReady = signal(0);
const listOf = (key: string): ListId => (key.startsWith('ar/') ? 'arabic' : key.startsWith('qr/') ? 'quran' : 'narration');
const clip = (key: string) =>
  key.startsWith('ar/') ? lists.arabic?.[key.slice(3)] : key.startsWith('qr/') ? lists.quran?.[key.slice(3)] : lists.narration?.[key];

function loadList(id: ListId): Promise<void> {
  if (lists[id]) return Promise.resolve();
  if (asking[id]) return asking[id];
  if (Date.now() - (failedAt[id] ?? -Infinity) < 30e3) return Promise.resolve();
  const stop = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((r) => (timer = setTimeout(() => (stop.abort(), r(null)), 15e3)));
  const got = fetch(LIST_URL[id](), { signal: stop.signal })
    .then((r) => (r.ok ? (r.json() as Promise<Manifest>) : null))
    .catch(() => null);
  return (asking[id] = Promise.race([got, late]).then((m) => {
    clearTimeout(timer);
    delete asking[id];
    if (m?.clips) {
      lists[id] = m.clips;
      if (id === 'quran') quranQuotes = (m as Manifest & { quotes?: Record<string, string[]> }).quotes ?? {};
      clipsReady.value++;
    } else failedAt[id] = Date.now();
  }));
}
/** Loads the lists of clips; settles once each has arrived or given up. */
export function loadManifest(): Promise<void> {
  return Promise.all([loadList('narration'), loadList('arabic'), loadList('quran')]).then(() => {});
}
/** Only the lists that these clips are in. */
const loadFor = (keys: string[]) => Promise.all([...new Set(keys.map(listOf))].map(loadList));

/** Whether a clip exists (false until its list has loaded). */
export function hasClip(key: string) {
  // Read while drawing, it draws again when a list arrives; a list that failed is asked for again (at most every 30 s).
  void clipsReady.value;
  const found = clip(key);
  if (!found) void loadList(listOf(key));
  return !!found;
}

/** The recitation of a verse shown on its own, as keys to play in order; none until its list has loaded (read while
 *  drawing, it draws again when the list arrives). */
export function recitationKeys(text: string): string[] {
  void clipsReady.value;
  const names = quranQuotes[clipId(text)];
  if (!names) void loadList('quran');
  return (names ?? []).map((n) => `qr/${n}`);
}

/** Call synchronously inside a tap handler so iPhones allow later playback. */
export function unlockNarration() {
  if (isSilent()) return;
  if (!audio) {
    audio = new Audio();
    audio.preload = 'auto';
  }
  audio.src = SILENT_WAV;
  void audio.play().catch(() => {});
  void loadManifest();
}

let token = 0;
let blobUrl: string | null = null;
/** Ends the wait for the clip playing now: when it ends, when another clip takes its place, or on stop. */
let endClip: (() => void) | null = null;
/** The clips being spoken now (the list given to `speak`), or null when quiet. */
export const speaking = signal<string[] | null>(null);

function release() {
  if (blobUrl) URL.revokeObjectURL(blobUrl);
  blobUrl = null;
}

/**
 * A clip is fetched whole and played from memory. Fetched whole, the service worker keeps it for offline use;
 * an audio element pointed at the address asks for it in pieces instead, which iPhones need answered piece by
 * piece and which the offline cache cannot store.
 */
async function clipSrc(key: string, v?: string): Promise<string> {
  const file = key.startsWith('ar/') ? `${arabicBase()}${key.slice(3)}.m4a` : key.startsWith('qr/') ? `${quranBase()}${key.slice(3)}.mp3` : `${base()}${key}.m4a`;
  const url = `${file}${v ? `?v=${v}` : ''}`;
  try {
    const r = await fetch(url);
    if (r.ok) return URL.createObjectURL(await r.blob());
  } catch {
    /* offline and never played: the element tries the address itself */
  }
  return url;
}

/** Play clips in order; resolves true when finished, false when stopped, replaced by other clips, or (for the Quran) cut
 *  short by an ayah that could not play. Missing clips are skipped. */
export async function speak(keys: string[], muted: boolean): Promise<boolean> {
  const my = ++token;
  speaking.value = keys;
  try {
    const whole = await play(my, keys, muted);
    return whole && my === token;
  } finally {
    if (my === token) speaking.value = null;
  }
}

async function play(my: number, keys: string[], muted: boolean): Promise<boolean> {
  await loadFor(keys);
  const available = keys.filter((k) => clip(k));
  for (const key of available) {
    if (my !== token) return false;
    const { dur, v } = clip(key)!;
    if (muted || isSilent() || !audio) {
      await new Promise((r) => setTimeout(r, dur * 1000 + 250));
      continue;
    }
    const src = await clipSrc(key, v);
    if (my !== token) {
      if (src.startsWith('blob:')) URL.revokeObjectURL(src);
      return false;
    }
    const played = await new Promise<boolean>((resolve) => {
      const a = audio!;
      let fallback: ReturnType<typeof setTimeout> | undefined;
      // Only this clip's own handlers and timer are cleared, never those of a clip that has taken its place.
      const done = (ok: boolean) => {
        clearTimeout(fallback);
        if (a.onended === ended) a.onended = a.onerror = null;
        if (endClip === ended) endClip = null;
        resolve(ok);
      };
      const ended = () => done(true);
      endClip?.();
      endClip = ended;
      a.onended = ended;
      a.onerror = () => done(false);
      release();
      if (src.startsWith('blob:')) blobUrl = src;
      a.src = src;
      a.play().catch(() => (fallback = setTimeout(() => done(false), dur * 1000)));
    });
    // A verse is never recited with an ayah left out: the recitation stops at one that could not play.
    if (!played && key.startsWith('qr/')) return false;
    await new Promise((r) => setTimeout(r, 200));
  }
  return true;
}

export function stopNarration() {
  token++;
  speaking.value = null;
  endClip?.();
  if (audio) {
    audio.pause();
    audio.removeAttribute('src');
  }
  release();
}

/** Total speaking time of a list of clips, seconds. */
export async function duration(keys: string[]) {
  await loadFor(keys);
  return keys.reduce((s, k) => s + (clip(k)?.dur ?? 0) + 0.2, 0);
}
